/**
 * Squash Levels on player cards (owner, 3 Nov 2026, COMPLIANCE D202,
 * SQUASH_LEVELS_CARDS_PLAN.md).
 *
 * The API owns the rule. On person rows it sends `squashLevelsSharing`
 * ("sharing" | "notSharing" | "noAccount") beside `squashLevel`, and BOTH only
 * where the rule allows (club mates, followers, admins, super admins and the
 * player; inside a singles squash or racketball competition that sends, the
 * club member list for members who play in one, and the profile; never an
 * under 18). A player who does not share never has their level sent. So the
 * website never works the status out (and never reads anybody's own setting
 * for it): it draws what arrived.
 *
 *   * sharing: today's level chip, nothing without a level;
 *   * notSharing or noAccount: a greyed, dashed, icon only chip, never a
 *     number, with a tooltip saying it in words;
 *   * absent: exactly today's chip (the API has dropped the level where it is
 *     not allowed), never anything new;
 *   * an unknown value: nothing (fail closed).
 *
 * Mirrors the app's squash_levels_cards.dart. Change both together. Pure:
 * imported explicitly (house style).
 */

const STATUSES = ['sharing', 'notSharing', 'noAccount'];

export const SQUASH_LEVELS_NOT_SHARING_TOOLTIP = 'Not sharing results with Squash Levels';
export const SQUASH_LEVELS_TURN_ON_LABEL = 'Turn on';
/** The Turn on dialog's header while the question loads; the API's heading replaces it. */
export const SQUASH_LEVELS_QUESTION_TITLE = 'Share your results with Squash Levels';

/** The status the API sent, null when absent, "unknown" for a value this build does not know. */
export function squashLevelsSharingOf(person) {
  const s = person?.squashLevelsSharing;
  if (s == null) return null;
  return STATUSES.includes(s) ? s : 'unknown';
}

/**
 * A real rating. Negative is the API's not-yet-rated marker; 0 is real, as on
 * the app (squash_level_visibility.dart), so the two clients agree. Every
 * place the website prints a level number decides with this.
 */
export const squashLevelIsReal = (level) => typeof level === 'number' && Number.isFinite(level) && level >= 0;
const hasLevel = squashLevelIsReal;

/**
 * What a row's chip shows. `allowed` is the surface's own gate (the sport
 * uses ratings, this is a singles row); `requireStatus` is for the club
 * member list, which marks only members the API sent the field for.
 */
export function squashLevelsChipFor(person, {allowed = false, requireStatus = false} = {}) {
  if (!allowed || !person) return null;
  const status = squashLevelsSharingOf(person);
  if (status === 'notSharing' || status === 'noAccount') return {kind: 'off', tooltip: SQUASH_LEVELS_NOT_SHARING_TOOLTIP};
  if (status === 'unknown') return null;
  if (status === null && requireStatus) return null;
  if (!hasLevel(person.squashLevel)) return null;
  return {kind: 'level', level: person.squashLevel, squashLevelId: person.squashLevelId ?? null};
}

/**
 * The one status line on a player's sheet or profile. Someone else gets words
 * only; you get Turn on (not sharing) or a plain switch (sharing). Nothing
 * when the API sent nothing. No line ever points at the profile.
 */
export function squashLevelsStatusFor(person, {isMe = false} = {}) {
  const status = squashLevelsSharingOf(person);
  if (status === null || status === 'unknown') return null;
  if (isMe && status !== 'noAccount') {
    return status === 'sharing'
        ? {status, sharing: true, title: 'You share your results', subtitle: 'With Squash Levels, for all your competitions.', action: 'switch'}
        : {status, sharing: false, title: "You don't share your results", subtitle: "Matches aren't sent to Squash Levels.", action: 'turnOn'};
  }
  switch (status) {
    case 'sharing':
      return {status, sharing: true, title: 'Shares results with Squash Levels', subtitle: null, action: null};
    case 'notSharing':
      return {status, sharing: false, title: "Doesn't share results with Squash Levels", subtitle: null, action: null};
    default:
      return {status, sharing: false, title: "Can't share with Squash Levels", subtitle: 'They have no account.', action: null};
  }
}

/**
 * GET competitionQuestions/account/squashLevels answers with the question in
 * the same shape as a pending item. Accepts a list too; garbage is nothing.
 */
export function accountQuestionFrom(response) {
  if (response && typeof response.key === 'string' && response.key) return response;
  const first = Array.isArray(response?.questions) ? response.questions[0] : null;
  return first && typeof first.key === 'string' && first.key ? first : null;
}

/** Opened from Turn on, the switch starts ON: the member just asked for it. */
export function squashLevelsTurnOnTicks(question) {
  return question?.key ? {[question.key]: true} : {};
}

/** PUT auth/me/externalServices. */
export function squashLevelsServicesBody(on) {
  return {sendToLevels: on === true};
}

/** What your own card shows once the API has accepted the save. */
export function squashLevelsSharingAfterSave(on) {
  return on === true ? 'sharing' : 'notSharing';
}

/**
 * Your own row after a change you just saved: the new status, and no number
 * once you stop sharing (the API would send none). Turning on has no number to
 * show until the next load.
 */
export function withSquashLevelsSharing(person, status) {
  if (!person || !status) return person ?? null;
  return {...person, squashLevelsSharing: status, squashLevel: status === 'sharing' ? person.squashLevel : null};
}
